// Hook personalizado para gestionar usuarios en el panel de administración
// Maneja la paginación, búsqueda y estado de la lista de usuarios

import { useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getAdminUsersPaginated } from '../services/adminService';

/**
 * Hook para obtener y gestionar usuarios con paginación
 * @param {object} options - Opciones adicionales del query
 * @returns {object} - Estado y funciones para gestionar usuarios
 */
export const useAdminUsers = (options = {}) => {
  // Estado local para paginación y búsqueda
  const [cursor, setCursor] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [orderBy, setOrderBy] = useState('updated_at');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Función para actualizar búsqueda con debounce
  const handleSearchChange = useCallback((value) => {
    setSearchText(value);
    // Resetear cursor cuando cambia la búsqueda
    setCursor(null);
    
    // Debounce simple de 500ms
    const timeoutId = setTimeout(() => {
      setDebouncedSearch(value);
    }, 500);
    
    return () => clearTimeout(timeoutId);
  }, []);

  // Query para obtener usuarios paginados
  const usersQuery = useQuery({
    queryKey: ['adminUsers', cursor, debouncedSearch, orderBy],
    queryFn: () => getAdminUsersPaginated({
      limit: 99,
      cursorId: cursor,
      searchText: debouncedSearch || null,
      orderBy
    }),
    staleTime: 1000 * 60 * 2, // 2 minutos
    gcTime: 1000 * 60 * 5, // 5 minutos
    refetchOnWindowFocus: false,
    retry: 1,
    ...options
  });

  // Función para cargar la siguiente página
  const loadNextPage = useCallback(() => {
    if (usersQuery.data?.nextCursor && !usersQuery.isFetching) {
      setCursor(usersQuery.data.nextCursor);
    }
  }, [usersQuery.data?.nextCursor, usersQuery.isFetching]);

  // Función para resetear a la primera página
  const resetToFirstPage = useCallback(() => {
    setCursor(null);
  }, []);

  // Función para cambiar el ordenamiento
  const handleOrderByChange = useCallback((newOrderBy) => {
    setOrderBy(newOrderBy);
    setCursor(null); // Resetear cursor al cambiar ordenamiento
  }, []);

  return {
    // Estado del query
    usersQuery,
    users: usersQuery.data?.users || [],
    isLoading: usersQuery.isFetching, // Usar isFetching para reflejar cualquier carga (inicial o refetch)
    isError: usersQuery.isError,
    error: usersQuery.error,

    // Estado de paginación
    hasNextPage: usersQuery.data?.hasMore || false,
    totalCount: usersQuery.data?.totalCount || 0,

    // Estado de búsqueda
    searchText,
    debouncedSearch,
    orderBy,

    // Funciones de control
    loadNextPage,
    resetToFirstPage,
    handleSearchChange,
    handleOrderByChange,
    setCursor,
    refetchUsers: () => usersQuery.refetch()
  };
};
